// src/store/thunks/branchDisplayThunks.js
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import branchDisplayService from "../../services/branchDisplayService";

// ============================================
// DISPLAY & BRANDING THUNKS
// ============================================

export const getBranchDisplayThunk = createAsyncThunk(
  "branchDisplay/getBranchDisplay",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getBranchDisplay(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateBrandingThunk = createAsyncThunk(
  "branchDisplay/updateBranding",
  async ({ branchCode, brandingData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateBranding(
        branchCode,
        brandingData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateBrandColorsThunk = createAsyncThunk(
  "branchDisplay/updateBrandColors",
  async ({ branchCode, colors }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateBrandColors(
        branchCode,
        colors
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateVisibilityThunk = createAsyncThunk(
  "branchDisplay/updateVisibility",
  async ({ branchCode, visibilitySettings }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateVisibility(
        branchCode,
        visibilitySettings
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// LOGO & BANNER THUNKS
// ============================================

export const uploadLogoThunk = createAsyncThunk(
  "branchDisplay/uploadLogo",
  async ({ branchCode, formData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.uploadLogo(
        branchCode,
        formData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteLogoThunk = createAsyncThunk(
  "branchDisplay/deleteLogo",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.deleteLogo(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const uploadBannerThunk = createAsyncThunk(
  "branchDisplay/uploadBanner",
  async ({ branchCode, formData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.uploadBanner(
        branchCode,
        formData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// STORE IMAGES THUNKS
// ============================================

export const getStoreImagesThunk = createAsyncThunk(
  "branchDisplay/getStoreImages",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getStoreImages(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const uploadStoreImagesThunk = createAsyncThunk(
  "branchDisplay/uploadStoreImages",
  async ({ branchCode, formData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.uploadStoreImages(
        branchCode,
        formData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateStoreImageThunk = createAsyncThunk(
  "branchDisplay/updateStoreImage",
  async ({ branchCode, imageId, imageData }, { rejectWithValue }) => {
    try {
      if (imageId === undefined || imageId === null) {
        return rejectWithValue("Image identifier is required");
      }
      const response = await branchDisplayService.updateStoreImage(
        branchCode,
        imageId,
        imageData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteStoreImageThunk = createAsyncThunk(
  "branchDisplay/deleteStoreImage",
  async ({ branchCode, imageId }, { rejectWithValue }) => {
    try {
      if (imageId === undefined || imageId === null) {
        return rejectWithValue("Image identifier is required");
      }
      const response = await branchDisplayService.deleteStoreImage(
        branchCode,
        imageId
      );
      return { ...response, imageId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const reorderStoreImagesThunk = createAsyncThunk(
  "branchDisplay/reorderStoreImages",
  async ({ branchCode, imageOrder }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.reorderStoreImages(
        branchCode,
        imageOrder
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const setPrimaryImageThunk = createAsyncThunk(
  "branchDisplay/setPrimaryImage",
  async ({ branchCode, imageId }, { rejectWithValue }) => {
    try {
      if (imageId === undefined || imageId === null) {
        return rejectWithValue("Image identifier is required");
      }
      const response = await branchDisplayService.setPrimaryImage(
        branchCode,
        imageId
      );
      return { ...response, imageId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// STORE VIDEOS THUNKS
// ============================================

export const getStoreVideosThunk = createAsyncThunk(
  "branchDisplay/getStoreVideos",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getStoreVideos(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addStoreVideoThunk = createAsyncThunk(
  "branchDisplay/addStoreVideo",
  async ({ branchCode, videoData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.addStoreVideo(
        branchCode,
        videoData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateStoreVideoThunk = createAsyncThunk(
  "branchDisplay/updateStoreVideo",
  async ({ branchCode, videoIndex, videoData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateStoreVideo(
        branchCode,
        videoIndex,
        videoData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteStoreVideoThunk = createAsyncThunk(
  "branchDisplay/deleteStoreVideo",
  async ({ branchCode, videoIndex }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.deleteStoreVideo(
        branchCode,
        videoIndex
      );
      return { ...response, videoIndex };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// HIGHLIGHTS THUNKS
// ============================================

export const getHighlightsThunk = createAsyncThunk(
  "branchDisplay/getHighlights",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getHighlights(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateHighlightsThunk = createAsyncThunk(
  "branchDisplay/updateHighlights",
  async ({ branchCode, highlights }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateHighlights(
        branchCode,
        highlights
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addHighlightThunk = createAsyncThunk(
  "branchDisplay/addHighlight",
  async ({ branchCode, highlight }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.addHighlight(
        branchCode,
        highlight
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteHighlightThunk = createAsyncThunk(
  "branchDisplay/deleteHighlight",
  async ({ branchCode, highlightIndex }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.deleteHighlight(
        branchCode,
        highlightIndex
      );
      return { ...response, highlightIndex };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// COLLECTIONS THUNKS
// ============================================

export const getCollectionsThunk = createAsyncThunk(
  "branchDisplay/getCollections",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getCollections(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateCollectionsThunk = createAsyncThunk(
  "branchDisplay/updateCollections",
  async ({ branchCode, collections }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateCollections(
        branchCode,
        collections
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// SERVICES THUNKS
// ============================================

export const getServicesThunk = createAsyncThunk(
  "branchDisplay/getServices",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getServices(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateServicesThunk = createAsyncThunk(
  "branchDisplay/updateServices",
  async ({ branchCode, services }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateServices(
        branchCode,
        services
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addServiceThunk = createAsyncThunk(
  "branchDisplay/addService",
  async ({ branchCode, service }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.addService(
        branchCode,
        service
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateServiceThunk = createAsyncThunk(
  "branchDisplay/updateService",
  async ({ branchCode, serviceId, serviceData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateService(
        branchCode,
        serviceId,
        serviceData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteServiceThunk = createAsyncThunk(
  "branchDisplay/deleteService",
  async ({ branchCode, serviceId }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.deleteService(
        branchCode,
        serviceId
      );
      return { ...response, serviceId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// CERTIFICATIONS THUNKS
// ============================================

export const getCertificationsThunk = createAsyncThunk(
  "branchDisplay/getCertifications",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getCertifications(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addCertificationThunk = createAsyncThunk(
  "branchDisplay/addCertification",
  async ({ branchCode, formData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.addCertification(
        branchCode,
        formData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateCertificationThunk = createAsyncThunk(
  "branchDisplay/updateCertification",
  async ({ branchCode, certificationId, data }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateCertification(
        branchCode,
        certificationId,
        data
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteCertificationThunk = createAsyncThunk(
  "branchDisplay/deleteCertification",
  async ({ branchCode, certificationId }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.deleteCertification(
        branchCode,
        certificationId
      );
      return { ...response, certificationId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// AWARDS THUNKS
// ============================================

export const getAwardsThunk = createAsyncThunk(
  "branchDisplay/getAwards",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getAwards(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addAwardThunk = createAsyncThunk(
  "branchDisplay/addAward",
  async ({ branchCode, formData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.addAward(
        branchCode,
        formData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateAwardThunk = createAsyncThunk(
  "branchDisplay/updateAward",
  async ({ branchCode, awardId, data }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateAward(
        branchCode,
        awardId,
        data
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteAwardThunk = createAsyncThunk(
  "branchDisplay/deleteAward",
  async ({ branchCode, awardId }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.deleteAward(
        branchCode,
        awardId
      );
      return { ...response, awardId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// TESTIMONIALS THUNKS
// ============================================

export const getTestimonialsThunk = createAsyncThunk(
  "branchDisplay/getTestimonials",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getTestimonials(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addTestimonialThunk = createAsyncThunk(
  "branchDisplay/addTestimonial",
  async ({ branchCode, formData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.addTestimonial(
        branchCode,
        formData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTestimonialThunk = createAsyncThunk(
  "branchDisplay/updateTestimonial",
  async ({ branchCode, testimonialIndex, data }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateTestimonial(
        branchCode,
        testimonialIndex,
        data
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteTestimonialThunk = createAsyncThunk(
  "branchDisplay/deleteTestimonial",
  async ({ branchCode, testimonialIndex }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.deleteTestimonial(
        branchCode,
        testimonialIndex
      );
      return { ...response, testimonialIndex };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// USPs THUNKS
// ============================================

export const updateUSPsThunk = createAsyncThunk(
  "branchDisplay/updateUSPs",
  async ({ branchCode, usps }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateUSPs(branchCode, usps);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// CONTACT INFO THUNKS
// ============================================

export const getContactInfoThunk = createAsyncThunk(
  "branchDisplay/getContactInfo",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getContactInfo(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateContactInfoThunk = createAsyncThunk(
  "branchDisplay/updateContactInfo",
  async ({ branchCode, contactInfo }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateContactInfo(
        branchCode,
        contactInfo
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateSocialMediaThunk = createAsyncThunk(
  "branchDisplay/updateSocialMedia",
  async ({ branchCode, socialMedia }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateSocialMedia(
        branchCode,
        socialMedia
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// TEAM MEMBERS THUNKS
// ============================================

export const getTeamMembersThunk = createAsyncThunk(
  "branchDisplay/getTeamMembers",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getTeamMembers(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addTeamMemberThunk = createAsyncThunk(
  "branchDisplay/addTeamMember",
  async ({ branchCode, formData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.addTeamMember(
        branchCode,
        formData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTeamMemberThunk = createAsyncThunk(
  "branchDisplay/updateTeamMember",
  async ({ branchCode, memberId, data }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateTeamMember(
        branchCode,
        memberId,
        data
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteTeamMemberThunk = createAsyncThunk(
  "branchDisplay/deleteTeamMember",
  async ({ branchCode, memberId }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.deleteTeamMember(
        branchCode,
        memberId
      );
      return { ...response, memberId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// STORE INFO THUNKS
// ============================================

export const getStoreInfoThunk = createAsyncThunk(
  "branchDisplay/getStoreInfo",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getStoreInfo(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateStoreInfoThunk = createAsyncThunk(
  "branchDisplay/updateStoreInfo",
  async ({ branchCode, storeInfo }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateStoreInfo(
        branchCode,
        storeInfo
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateFacilitiesThunk = createAsyncThunk(
  "branchDisplay/updateFacilities",
  async ({ branchCode, facilities }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateFacilities(
        branchCode,
        facilities
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updatePaymentMethodsThunk = createAsyncThunk(
  "branchDisplay/updatePaymentMethods",
  async ({ branchCode, paymentMethods }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updatePaymentMethods(
        branchCode,
        paymentMethods
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateDirectionsThunk = createAsyncThunk(
  "branchDisplay/updateDirections",
  async ({ branchCode, directionsData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateDirections(
        branchCode,
        directionsData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Alias for updateDirections with full params (directions, nearbyLandmarks, parking)
export const updateStoreDirectionsThunk = createAsyncThunk(
  "branchDisplay/updateStoreDirections",
  async (
    { branchCode, directions, nearbyLandmarks, parking },
    { rejectWithValue }
  ) => {
    try {
      const response = await branchDisplayService.updateDirections(branchCode, {
        directions,
        nearbyLandmarks,
        parking,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update full address (line1, line2, city, state, pinCode, landmark, lat/lon, googleMapsUrl, googlePlaceId)
export const updateAddressThunk = createAsyncThunk(
  "branchDisplay/updateAddress",
  async ({ branchCode, addressData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateAddress(
        branchCode,
        addressData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// SEO THUNKS
// ============================================

export const getSEOThunk = createAsyncThunk(
  "branchDisplay/getSEO",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getSEO(branchCode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateSEOThunk = createAsyncThunk(
  "branchDisplay/updateSEO",
  async ({ branchCode, seoData }, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.updateSEO(
        branchCode,
        seoData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// BULK OPERATIONS THUNKS
// ============================================

export const copyDisplayConfigThunk = createAsyncThunk(
  "branchDisplay/copyDisplayConfig",
  async (
    { sourceBranchCode, targetBranchCodes, sections },
    { rejectWithValue }
  ) => {
    try {
      const response = await branchDisplayService.copyDisplayConfig(
        sourceBranchCode,
        targetBranchCodes,
        sections
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getDisplayOptionsThunk = createAsyncThunk(
  "branchDisplay/getDisplayOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await branchDisplayService.getDisplayOptions();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================
// ACTIONS
// ============================================

export const clearBranchDisplayError = createAction("branchDisplay/clearError");
export const resetBranchDisplay = createAction("branchDisplay/reset");
export const setActiveSection = createAction("branchDisplay/setActiveSection");
export const setEditMode = createAction("branchDisplay/setEditMode");
